/* ─────────  VisitLogger.tsx  ───────── */
import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

const KEY_VISIT_ENDPOINT = 'https://keyvisit.businessa.com';
const COOKIE_NAME        = 'BA_FOR_VISIT';
const COOKIE_NAME2        = 'BA_CLIENT_VISIT';
const COOKIE_DAYS        = 3000;
const SEND_INTERVAL_MS   = 1_000;        // once per second per key

/* ---------- cookie helpers ---------- */

const setCookie = (k: string, v: string, d: number) => {
  const expires = new Date(Date.now() + d * 864e5).toUTCString();
  document.cookie = `${k}=${encodeURIComponent(v)};expires=${expires};path=/`;
};

const getCookie = (k: string) =>
  document.cookie
    .split('; ')
    .find((c) => c.startsWith(`${k}=`))
    ?.split('=')[1];

/* ---------- user-interaction helper ---------- */

const useHasInteracted = () => {
  const [interacted, setInteracted] = useState(false);

  useEffect(() => {
    if (interacted) return;                    // already true → nothing to do

    const mark = () => setInteracted(true);
    const opts: AddEventListenerOptions = { once: true, passive: true };

    window.addEventListener('mousemove',  mark, opts);
    window.addEventListener('mousedown',  mark, opts);
    window.addEventListener('keydown',    mark, opts);
    window.addEventListener('touchstart', mark, opts);
    window.addEventListener('scroll',     mark, opts);

    return () => {
      window.removeEventListener('mousemove',  mark);
      window.removeEventListener('mousedown',  mark);
      window.removeEventListener('keydown',    mark);
      window.removeEventListener('touchstart', mark);
      window.removeEventListener('scroll',     mark);
    };
  }, [interacted]);

  return interacted;
};

/* ---------- VisitLogger ---------- */

/*  module-level cache – survives component rerenders/unmounts  */
const lastSent: Record<string, number> = {};

const VisitLogger: React.FC = () => {
  const loc          = useLocation();
  const hasInteracted = useHasInteracted();

  useEffect(() => {
    /*  don’t do anything unless we have real user interaction  */
    if (!hasInteracted) return;

    const params     = new URLSearchParams(loc.search);
    const fromQuery  = params.get('for') || '';
    const fromCookie = getCookie(COOKIE_NAME) || '';
    const fromClientCookie = getCookie(COOKIE_NAME2) || '';
    const id         = fromQuery || fromCookie || fromClientCookie;
    if (!id) return;

    /*  refresh cookie if id came from query string  */
    if (fromQuery) setCookie(COOKIE_NAME, fromQuery, COOKIE_DAYS);

    const cacheKey = `${id}|${loc.pathname}`;
    const now      = Date.now();

    /*  throttle duplicate calls (same key) to 1 / second  */
    if (lastSent[cacheKey] && now - lastSent[cacheKey] < SEND_INTERVAL_MS) {
      return;
    }
    lastSent[cacheKey] = now;

    const url =
      `${KEY_VISIT_ENDPOINT}?for=${encodeURIComponent(id)}` +
      `&page=${encodeURIComponent(loc.pathname)}`;

    fetch(url, { method: 'GET', mode: 'no-cors' }).catch(() => {});
  }, [loc, hasInteracted]);

  return null;
};

export default VisitLogger;
